import {
  Group,
  Title,
  Text,
  Button,
  ActionIcon,
  Menu,
  Stack,
  rem,
  Divider,
  Tooltip,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconArrowLeft,
  IconDots,
  IconEdit,
  IconTrash,
  IconCopy,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs, BreadcrumbItem } from "./Breadcrumbs";
import { StatusBadge } from "./StatusBadge";

const useStyles = createStyles((theme) => ({
  root: {
    marginBottom: theme.spacing.lg,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing.md,
  },
  titleSection: {
    flex: 1,
    minWidth: rem(200),
  },
  title: {
    fontSize: rem(24),
    fontWeight: 700,
    color: theme.colors.dark[0],
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[2],
    marginTop: rem(4),
  },
  backButton: {
    color: theme.colors.dark[2],
    "&:hover": {
      color: theme.colors.cyan[4],
      backgroundColor: theme.colors.dark[6],
    },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
    flexWrap: "wrap",
  },
  divider: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderColor: theme.colors.dark[5],
  },
}));

interface PageAction {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  href?: string;
  color?: string;
  variant?: "filled" | "outline" | "light" | "subtle";
  disabled?: boolean;
  loading?: boolean;
}

interface MenuAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  color?: string;
  disabled?: boolean;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  status?: string;
  statusColor?: string;
  breadcrumbs?: BreadcrumbItem[];
  showBreadcrumbs?: boolean;
  showBackButton?: boolean;
  backPath?: string;
  backLabel?: string;
  actions?: PageAction[];
  menuActions?: MenuAction[];
  children?: ReactNode;
  showDivider?: boolean;
  compact?: boolean;
  className?: string;
}

export const PageHeader = ({
  title,
  subtitle,
  status,
  statusColor,
  breadcrumbs,
  showBreadcrumbs = true,
  showBackButton = false,
  backPath,
  backLabel = "Back",
  actions = [],
  menuActions = [],
  children,
  showDivider = false,
  compact = false,
  className,
}: PageHeaderProps) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className={`${classes.root} ${className ?? ""}`}>
      {/* Breadcrumbs */}
      {showBreadcrumbs && <Breadcrumbs items={breadcrumbs} />}

      {/* Header */}
      <div className={classes.header}>
        {/* Title section */}
        <div className={classes.titleSection}>
          <Group gap="sm" align="center">
            {showBackButton && (
              <Tooltip label={backLabel}>
                <ActionIcon
                  className={classes.backButton}
                  size="lg"
                  radius="md"
                  onClick={handleBack}
                >
                  <IconArrowLeft size={20} />
                </ActionIcon>
              </Tooltip>
            )}

            <Stack gap={0}>
              <Group gap="sm" align="center">
                <Title order={compact ? 4 : 2} className={classes.title}>
                  {title}
                </Title>
                {status && <StatusBadge status={status} color={statusColor} />}
              </Group>

              {subtitle && <Text className={classes.subtitle}>{subtitle}</Text>}
            </Stack>
          </Group>
        </div>

        {/* Actions */}
        {(actions.length > 0 || menuActions.length > 0) && (
          <div className={classes.actions}>
            {actions.map((action, index) => (
              <Button
                key={index}
                leftSection={action.icon}
                color={action.color}
                variant={action.variant ?? "filled"}
                onClick={action.onClick}
                disabled={action.disabled}
                loading={action.loading}
                size={compact ? "xs" : "sm"}
              >
                {action.label}
              </Button>
            ))}

            {menuActions.length > 0 && (
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="light" size={compact ? "md" : "lg"}>
                    <IconDots size={18} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  {menuActions.map((action, index) => (
                    <Menu.Item
                      key={index}
                      leftSection={action.icon}
                      color={action.color}
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      {action.label}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            )}
          </div>
        )}
      </div>

      {/* Custom content */}
      {children}

      {/* Divider */}
      {showDivider && <Divider className={classes.divider} />}
    </div>
  );
};

// Pre-configured page headers for common patterns
export const ListPageHeader = ({
  title,
  createLabel = "Create New",
  onCreate,
  ...props
}: Partial<PageHeaderProps> & {
  title: string;
  createLabel?: string;
  onCreate?: () => void;
}) => (
  <PageHeader
    title={title}
    actions={
      onCreate
        ? [
            {
              label: createLabel,
              icon: <IconEdit size={16} />,
              onClick: onCreate,
            },
          ]
        : []
    }
    {...props}
  />
);

export const DetailPageHeader = ({
  title,
  onEdit,
  onDelete,
  onDuplicate,
  onRefresh,
  onDownload,
  ...props
}: Partial<PageHeaderProps> & {
  title: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onRefresh?: () => void;
  onDownload?: () => void;
}) => {
  const menuActions: MenuAction[] = [];

  if (onDuplicate) {
    menuActions.push({
      label: "Duplicate",
      icon: <IconCopy size={14} />,
      onClick: onDuplicate,
    });
  }

  if (onDownload) {
    menuActions.push({
      label: "Download",
      icon: <IconDownload size={14} />,
      onClick: onDownload,
    });
  }

  if (onRefresh) {
    menuActions.push({
      label: "Refresh",
      icon: <IconRefresh size={14} />,
      onClick: onRefresh,
    });
  }

  if (onDelete) {
    menuActions.push({
      label: "Delete",
      icon: <IconTrash size={14} />,
      color: "red",
      onClick: onDelete,
    });
  }

  return (
    <PageHeader
      title={title}
      showBackButton
      actions={
        onEdit
          ? [
              {
                label: "Edit",
                icon: <IconEdit size={16} />,
                onClick: onEdit,
              },
            ]
          : []
      }
      menuActions={menuActions}
      {...props}
    />
  );
};
